import { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';

import {
  useGetResourceDetailQuery,
  useGetResourceTypeSchemaQuery,
} from '../store/hooks';

interface ResourceSchema {
  json_schema?: { properties?: Record<string, { title?: string }> };
}

interface RendererProps {
  data: Record<string, unknown>;
  schema?: ResourceSchema;
}

function humanize(key: string): string {
  return key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function isIsoDate(v: string): boolean {
  return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(v);
}

/** Recursively render an arbitrary JSON value from a resource payload. */
function ResourceValue({ value }: { value: unknown }) {
  if (value === null || value === undefined || value === '') {
    return (
      <Typography component="span" color="text.secondary">
        —
      </Typography>
    );
  }
  if (typeof value === 'boolean') {
    return (
      <Chip
        size="small"
        label={value ? 'true' : 'false'}
        color={value ? 'success' : 'default'}
      />
    );
  }
  if (typeof value === 'number') {
    return <span>{value}</span>;
  }
  if (typeof value === 'string') {
    return <span>{isIsoDate(value) ? new Date(value).toLocaleString() : value}</span>;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return (
        <Typography component="span" color="text.secondary">
          (empty)
        </Typography>
      );
    }
    return (
      <Box component="ul" sx={{ m: 0, pl: 2 }}>
        {(value as unknown[]).map((item, i) => (
          <li key={i}>
            <ResourceValue value={item} />
          </li>
        ))}
      </Box>
    );
  }
  if (typeof value === 'object') {
    return <KeyValueTable obj={value as Record<string, unknown>} />;
  }
  return null;
}

function KeyValueTable({
  obj,
  labels,
}: {
  obj: Record<string, unknown>;
  labels?: Record<string, string>;
}) {
  const keys = Object.keys(obj);
  if (keys.length === 0) {
    return (
      <Typography component="span" color="text.secondary">
        (empty)
      </Typography>
    );
  }
  return (
    <Table
      size="small"
      sx={{
        '& td': {
          borderBottom: '1px solid',
          borderColor: 'divider',
          verticalAlign: 'top',
        },
      }}
    >
      <TableBody>
        {keys.map(k => (
          <TableRow key={k}>
            <TableCell
              sx={{ width: 240, fontWeight: 500, color: 'text.secondary' }}
            >
              {labels?.[k] ?? humanize(k)}
            </TableCell>
            <TableCell>
              <ResourceValue value={obj[k]} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

/** Default renderer: labeled key/value driven by the resource's JSON Schema. */
function SchemaResourceView({ data, schema }: RendererProps) {
  const props = schema?.json_schema?.properties ?? {};
  const labels: Record<string, string> = {};
  for (const [k, v] of Object.entries(props)) {
    if (v && typeof v.title === 'string') labels[k] = v.title;
  }
  return <KeyValueTable obj={data} labels={labels} />;
}

/**
 * Renderer registry: `resource_type → component`. Per-type custom renderers
 * register here (GDN-1923); everything falls back to the schema-driven default.
 */
const RESOURCE_RENDERERS: Record<
  string,
  (props: RendererProps) => React.ReactElement
> = {};

interface ResourceDetailViewProps {
  resourceType: string;
  resourceId: string;
  organizationId?: string;
}

/**
 * Reads one resource via the governed `Resources` registry and renders it with
 * the per-type renderer (or the schema-driven default), plus a Raw JSON toggle.
 */
export function ResourceDetailView({
  resourceType,
  resourceId,
  organizationId,
}: ResourceDetailViewProps) {
  const [raw, setRaw] = useState(false);

  const { data: detail, isLoading, error } = useGetResourceDetailQuery({
    resourceType,
    resourceId,
    organizationId,
  });
  const { data: schema } = useGetResourceTypeSchemaQuery({ resourceType });

  const data = (detail?.data ?? {}) as Record<string, unknown>;
  const Renderer = RESOURCE_RENDERERS[resourceType] ?? SchemaResourceView;

  return (
    <Card>
      <CardHeader
        title={resourceType}
        subheader={resourceId}
        action={
          <Button size="small" onClick={() => setRaw(r => !r)}>
            {raw ? 'Formatted' : 'Raw JSON'}
          </Button>
        }
      />
      <CardContent>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : error ? (
          <Typography color="error">
            Could not load this resource (it may not exist or be out of scope).
          </Typography>
        ) : raw ? (
          <pre
            style={{
              margin: 0,
              fontSize: 12,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
            }}
          >
            {JSON.stringify(data, null, 2)}
          </pre>
        ) : (
          <Renderer data={data} schema={schema} />
        )}
      </CardContent>
    </Card>
  );
}
